<template>
  <el-row>
    <el-row class="title-bar vertical-center-box" type="flex" justify="space-between">
      <div class="title-h4">백업 목록</div>
      <div>
        <w-header-search-button @click="onSnapshotSearchToolVisible"></w-header-search-button>
      </div>
    </el-row>
    <div>
      <el-table-wrapper ref="snapshotTable" v-loading="loading" :column-default="columnDefault" :columns="snapshotsColumns" :data="snapshots" :pagination="true" highlight-current-row
        :show-search-tool="snapshotSearchToolVisible" :show-custom-header="true">
        <template slot-scope="scope" slot="snapshot-time-slot">
          {{ getDateToString(scope.row['start_time_in_millis'], 'YYYY-MM-DD HH:mm:ss') }}
        </template>
        <template slot-scope="scope" slot="restore-slot">
          <el-row type="flex" justify="center">
            <el-button v-if="processingRestoreIndex !== scope.$index" size="mini" @click="restoreDataindex(scope.row.snapshot, scope.$index)">복구</el-button>
            <i v-if="processingRestoreIndex === scope.$index" class="el-icon-loading"></i>
          </el-row>
        </template>
      </el-table-wrapper>
    </div>
  </el-row>
</template>

<script>
import { isEmpty, isNil } from 'lodash';

import LogStoreAPI from '@/api/log/log-store';
import { columnDefault } from '@/constants/table';
import { getDateToString } from '@/utils/date';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import msgUtils from '@/utils/message';

const snapshotStatusCheckTask = 'snapshotStatusCheckTask';

export default {
  name: 'WSnapshotTable',
  props: {
    selectedDataindex: Object,
    isProgressedSnapshot: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      snapshots: [],
      snapshotsColumns: [
        {
          prop: 'snapshot.name',
          label: '저장소명',
          searchable: true,
          sortable: true,
        },
        {
          prop: 'state',
          label: '상태',
          searchable: true,
          sortable: true,
          width: 160,
          formatter: this.getStringFormatter,
        },
        {
          prop: 'start_time_in_millis',
          label: '백업 시각',
          scopedSlot: 'snapshot-time-slot',
          width: 160,
          searchable: true,
          searchType: 'date',
          sortable: true,
        },
        {
          label: '복구',
          width: 60,
          scopedSlot: 'restore-slot',
        },
      ],
      columnDefault,
      snapshotSearchToolVisible: false,
      isProgressedRestore: false,
      processingRestoreIndex: null,
    };
  },
  watch: {
    selectedDataindex(value) {
      if (!isEmpty(value) && !isNil(value.dataindexOrigin)) {
        this.loadData(value.dataindexOrigin);
      }
    },
    isProgressedRestore(value) {
      this.$emit('change-restore-state', value);
    },
  },
  beforeDestroy() {
    this.$taskManager.removeTask(snapshotStatusCheckTask);
  },
  methods: {
    getDateToString,
    getValueAfterCheckEmpty,
    loadData(dataindexOrigin) {
      if (isEmpty(dataindexOrigin)) {
        dataindexOrigin = 'log';
      }
      const params = {
        params: {
          dataindexOrigin,
        },
      };
      this.loading = true;
      LogStoreAPI.getSnapshots(params).then((response) => {
        if (response.data.success) {
          this.snapshots = response.data.data;
        } else {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '백업 목록', false));
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '백업 목록', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    restoreDataindex(snapshotName, index) {
      const alreadyProcessingRestore = '이미 백업 또는 복구를 진행 중인 저장소가 있습니다. 작업이 완료될 때까지 다른 저장소의 백업 및 복구가 불가능합니다.';
      const successRestore = '저장소를 복구하였습니다.';
      const failRestore = '저장소를 복구하지 못하였습니다.';

      if (this.isProgressedSnapshot || this.isProgressedRestore) {
        this.$message(msgUtils.getCustomMsgData(alreadyProcessingRestore));
        return;
      }
      const snapshotParams = {
        snapshotName: snapshotName.name,
      };
      const params = {
        indexName: snapshotName.name,
      };
      this.$confirm(...msgUtils.getCustomConfirmData('복구된 저장소는 백업 목록에서 삭제됩니다.')).then(() => {
        this.processingRestoreIndex = index;
        this.$message(msgUtils.getCustomMsgData('복구를 진행합니다. 작업이 완료될 때까지 다른 저장소의 백업 및 복구가 불가능합니다.'));
        this.isProgressedRestore = true;
        LogStoreAPI.restoreDataindex({ snapshotName: snapshotName.name }).then((response) => {
          if (response.data.success && response.data.data === 'SUCCESS') {
            this.deleteSnapshot({ snapshotName: snapshotName.name }).then((delSnapshotRes) => {
              if (delSnapshotRes.data.success && delSnapshotRes.data.data) {
                this.$emit('update-index');
                this.$message(msgUtils.getCustomResultMsgData(successRestore, response.data.success));
                this.processingRestoreIndex = null;
                this.isProgressedRestore = false;
                this.loadData(this.selectedDataindex.dataindexOrigin);
              } else {
                // 복구는 했으나 스냅샷 삭제 실패함 > 복구된 인덱스를 다시 삭제함 (복구 실패)
                this.deleteIndex(params).then((delIndexRes) => {
                  if (delIndexRes.data.success && delIndexRes.data.data) {
                    this.$message(msgUtils.getCustomResultMsgData(failRestore, false));
                    this.processingRestoreIndex = null;
                    this.isProgressedRestore = false;
                  }
                });
              }
            });
          } else if (response.data.success && response.data.data === 'IN_PROGRESS') {
            this.$message(msgUtils.getCustomMsgData(alreadyProcessingRestore));
          } else {
            this.$taskManager.createTask(snapshotStatusCheckTask, 3, () => {
              this.checkRestoreStatus(params).then((statusRes) => {
                if (statusRes.data.success && statusRes.data.data === 'SUCCESS') {
                  this.$taskManager.removeTask(snapshotStatusCheckTask);
                  this.deleteSnapshot(snapshotParams).then((delSnapshotRes) => {
                    if (delSnapshotRes.data.success && delSnapshotRes.data.data) {
                      this.$emit('update-index');
                      this.$message(msgUtils.getCustomResultMsgData(successRestore, true));
                      this.processingRestoreIndex = null;
                      this.isProgressedRestore = false;
                      this.loadData(this.selectedDataindex.dataindexOrigin);
                    } else {
                      this.deleteIndex(params).then((delIndexRes) => {
                        this.$emit('update-index');
                        this.$message(msgUtils.getCustomResultMsgData(failRestore, false));
                        if (delIndexRes.data.success && delIndexRes.data.data) {
                          this.processingRestoreIndex = null;
                          this.isProgressedRestore = false;
                        }
                      });
                    }
                  });
                } else if (!statusRes.data.success || (statusRes.data.data !== 'IN_PROGRESS' && statusRes.data.data !== 'SUCCESS')) {
                  this.$taskManager.removeTask(snapshotStatusCheckTask);
                  this.$message(msgUtils.getCustomResultMsgData(failRestore, false));
                  this.processingRestoreIndex = null;
                  this.isProgressedRestore = false;
                  this.deleteIndex(params);
                }
              });
            });
            this.$taskManager.startTask(snapshotStatusCheckTask);
          }
        }).catch((e) => {
          this.$message(msgUtils.getCustomResultMsgData(failRestore, false));
          this.processingRestoreIndex = null;
          this.isProgressedRestore = false;
          throw new Error(e);
        });
      }).catch(() => {});
    },
    getStringFormatter(row, col, value) {
      if (isEmpty(value)) {
        return EMPTY_TEXT;
      } else if (value === 'SUCCESS') {
        return '성공';
      } else if (value === 'IN_PROGRESS') {
        return '백업 중';
      } else if (value === 'PARTIAL') {
        return '일부 성공';
      } else if (value === 'FAILED') {
        return '실패';
      }
      return EMPTY_TEXT;
    },
    onSnapshotSearchToolVisible() {
      this.snapshotSearchToolVisible = !this.snapshotSearchToolVisible;
    },
    checkRestoreStatus(params) {
      return LogStoreAPI.checkRestoreStatus(params);
    },
    deleteSnapshot(params) {
      return LogStoreAPI.deleteSnapshot(params);
    },
    deleteIndex(params) {
      return LogStoreAPI.deleteIndex(params);
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/log-analysis/popup/store/SnapshotTable.vue